<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import EmptyState from '@/components/ui/empty-state/EmptyState.vue';
import GroupedEntityList from '@/components/workspace/GroupedEntityList.vue';
import { ChevronDown, ChevronRight, ClipboardPenLine, Copy, Loader2, Mail, MoreHorizontal } from 'lucide-vue-next';
import FormSubmissionAnswerList from '@/components/forms/FormSubmissionAnswerList.vue';
import { toast } from 'vue-sonner';
import { copyToClipboard } from '~/lib/clipboard';
import { formatRelativeTime } from '~/lib/formatters';
import { buildClientPortalLink } from '~/lib/tenantLinks';
import type { PortalFormRequest } from '~/types/models';

const props = withDefaults(defineProps<{
    items: PortalFormRequest[];
    loading?: boolean;
    limit?: number;
    showClient?: boolean;
    showForm?: boolean;
    showProject?: boolean;
    emptyTitle?: string;
    emptyDescription?: string;
    contained?: boolean;
    detailsMode?: 'inline' | 'emit';
    portalOrigin?: string;
    tenantId?: string;
    clientSlug?: string;
    canManage?: boolean;
}>(), {
    loading: false,
    limit: 0,
    showClient: true,
    showForm: true,
    showProject: true,
    emptyTitle: 'No forms sent',
    emptyDescription: 'Forms sent to the client will appear here until they are completed.',
    contained: true,
    detailsMode: 'inline',
    portalOrigin: '',
    tenantId: '',
    clientSlug: '',
    canManage: false,
});

const emit = defineEmits<{ view: [request: PortalFormRequest] }>();

const expandedIds = ref<string[]>([]);
const resendingRequestId = ref('');
const formStore = useFormStore();
const { $auth } = useNuxtApp();
const visibleItems = computed(() => props.limit > 0 ? props.items.slice(0, props.limit) : props.items);

function portalLink(request: PortalFormRequest): string {
    const origin = props.portalOrigin || (typeof window !== 'undefined' ? window.location.origin : '');
    const tenantId = request.tenantId || props.tenantId;
    if (request.accessTokenId && origin && tenantId) {
        const url = new URL(`/public/forms/${encodeURIComponent(request.accessTokenId)}`, origin);
        url.searchParams.set('tenantId', tenantId);
        return url.toString();
    }
    const clientSlug = request.clientSlug || props.clientSlug;
    if (!origin || !tenantId || !clientSlug || !request.id) return '';

    return buildClientPortalLink({
        origin,
        tenantId,
        clientSlug,
        section: 'forms',
        resourceId: request.id,
    });
}

async function copyPortalLink(request: PortalFormRequest) {
    const link = portalLink(request);
    if (!link) {
        toast.error('Unable to create a portal link for this form.');
        return;
    }

    try {
        await copyToClipboard(link);
        toast.success('Portal link copied to clipboard.');
    } catch (error: any) {
        toast.error(error?.message || 'Unable to copy the portal link.');
    }
}

async function resendFormEmail(request: PortalFormRequest) {
    const tenantId = request.tenantId || props.tenantId;
    if (!tenantId || !request.formId || !request.clientId) {
        toast.error('This form is missing the details needed to resend it.');
        return;
    }

    resendingRequestId.value = request.id;
    try {
        const token = await $auth.currentUser?.getIdToken();
        if (!token) throw new Error('You need to be signed in to resend a form.');

        const response = await $fetch<{
            recipientEmail?: string;
            deliveryStatus?: 'sent' | 'skipped' | 'not_requested';
        }>('/api/forms/send-invite', {
            method: 'POST',
            headers: { authorization: `Bearer ${token}` },
            body: {
                tenantId,
                formId: request.formId,
                linkedClientId: request.clientId,
                linkedProjectId: request.projectId || undefined,
                deliverEmail: true,
                accessMode: 'secure',
                preferPortalAssignment: request.trackingSource !== 'email_invite',
            },
        });

        if (response.deliveryStatus === 'skipped') {
            toast.success('Email delivery is not configured here yet. Copy the portal link to send it manually.');
        } else {
            toast.success(response.recipientEmail
                ? `Form link emailed to ${response.recipientEmail}.`
                : 'Form link emailed.');
        }
        await formStore.fetchPortalRequests({ silent: true });
    } catch (error: any) {
        toast.error(error?.data?.statusMessage || error?.message || 'Unable to resend the form email.');
    } finally {
        resendingRequestId.value = '';
    }
}

function isExpanded(requestId: string): boolean {
    return expandedIds.value.includes(requestId);
}

function toggle(requestId: string) {
    expandedIds.value = isExpanded(requestId)
        ? expandedIds.value.filter(id => id !== requestId)
        : [...expandedIds.value, requestId];
}

function viewAnswers(request: PortalFormRequest) {
    if (props.detailsMode === 'emit') {
        emit('view', request);
        return;
    }
    toggle(request.id);
}

function progressLabel(request: PortalFormRequest): string {
    if (request.status === 'pending') return request.openedAt ? 'Opened by client' : 'Not opened yet';
    if (request.status === 'completed') return 'Response submitted';
    if (request.status === 'cancelled') return 'Invite cancelled';
    const count = request.draftAnswers?.length || 0;
    return `${count} answer${count === 1 ? '' : 's'} saved`;
}

function statusLabel(request: PortalFormRequest): string {
    if (request.status === 'pending') return request.openedAt ? 'Opened' : 'Sent';
    if (request.status === 'completed') return 'Completed';
    if (request.status === 'cancelled') return 'Cancelled';
    return 'In progress';
}

function timestampLabel(request: PortalFormRequest): string {
    if (request.status === 'pending') {
        if (request.openedAt) return `Opened ${formatRelativeTime(request.openedAt)}`;
        return `Sent ${formatRelativeTime(request.sentAt || request.createdAt || request.updatedAt)}`;
    }
    if (request.status === 'completed') return `Completed ${formatRelativeTime(request.completedAt || request.updatedAt)}`;
    return `Updated ${formatRelativeTime(request.draftUpdatedAt || request.updatedAt)}`;
}
</script>

<template>
    <GroupedEntityList :contained="contained" :empty="!loading && visibleItems.length === 0">
        <template #empty>
            <EmptyState embedded :title="emptyTitle" :description="emptyDescription">
                <template #icon>
                    <ClipboardPenLine class="size-8 text-muted-foreground/70" />
                </template>
            </EmptyState>
        </template>

        <div v-if="loading" class="py-8 text-center text-sm text-muted-foreground">
            Loading form progress...
        </div>

        <template v-else>
          <div v-for="request in visibleItems" :key="request.id">
            <div class="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div class="min-w-0 space-y-1.5">
                    <div class="flex flex-wrap items-center gap-2">
                        <p v-if="showForm" class="truncate text-sm font-medium text-foreground">{{ request.formName || 'Portal form' }}</p>
                        <Badge variant="secondary">{{ statusLabel(request) }}</Badge>
                        <Badge variant="outline">{{ progressLabel(request) }}</Badge>
                    </div>
                    <p class="text-xs text-muted-foreground">
                        <span v-if="showClient">{{ request.clientName || 'Client' }}</span>
                        <span v-if="showClient && request.projectName"> · </span>
                        <span v-if="showProject && request.projectName">{{ request.projectName }}</span>
                        <span v-if="showClient || (showProject && request.projectName)"> · </span>
                        {{ timestampLabel(request) }}
                    </p>
                </div>

                <div class="flex shrink-0 items-center gap-1 sm:ml-auto">
                    <Button type="button" variant="ghost" size="sm" @click="viewAnswers(request)">
                        <ChevronDown v-if="isExpanded(request.id)" class="mr-2 size-4" />
                        <ChevronRight v-else class="mr-2 size-4" />
                        {{ detailsMode === 'inline' && isExpanded(request.id) ? 'Hide details' : (request.status === 'pending' ? 'View request' : request.status === 'completed' ? 'View response' : 'View answers') }}
                    </Button>
                    <DropdownMenu v-if="canManage && portalLink(request)">
                        <DropdownMenuTrigger as-child>
                            <Button type="button" variant="ghost" size="icon" class="shrink-0" aria-label="More form actions">
                                <MoreHorizontal class="size-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" class="w-56">
                            <DropdownMenuItem @select="copyPortalLink(request)">
                                <Copy class="mr-2 size-4" />Copy portal link
                            </DropdownMenuItem>
                            <DropdownMenuItem :disabled="Boolean(resendingRequestId)" @select="resendFormEmail(request)">
                                <Loader2 v-if="resendingRequestId === request.id" class="mr-2 size-4 animate-spin" />
                                <Mail v-else class="mr-2 size-4" />Resend form email
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <div v-if="detailsMode === 'inline' && isExpanded(request.id)" class="border-t border-border/50 bg-muted/20 px-4 py-3">
                <p class="mb-2 text-xs text-muted-foreground">
                    {{ request.status === 'completed' ? 'The client submitted this form.' : request.status === 'pending' ? (request.openedAt ? 'The client opened this form and has not saved answers yet.' : 'This form has been sent and is waiting for the client to open it.') : 'This is an autosaved draft. The client may still change these answers before submitting.' }}
                </p>
                <FormSubmissionAnswerList
                    :answers="request.draftAnswers || []"
                    surface="plain"
                    :empty-message="request.status === 'pending' && !request.openedAt ? 'No answers yet. The client has not opened this form.' : 'The client has opened this form but has not entered an answer yet.'"
                />
            </div>
          </div>
        </template>
    </GroupedEntityList>
</template>
